import React, { useRef, useState, useEffect, useCallback } from 'react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Đường cong Nhiệt độ – Nhiệt lượng (Lớp 12 — Bài 5 nóng chảy / Bài 6 hoá hơi):
// rắn → (nóng chảy, đoạn ngang Lf) → lỏng → (sôi/hoá hơi, đoạn ngang Lv) → hơi.
// Kéo thanh trượt nhiệt lượng Q → điểm chạy trên đường cong + hiển thị trạng thái.
const SEG = [0, 0.15, 0.35, 0.55, 0.85, 1]   // mốc Q chuẩn hoá
const T0 = -20, Tm = 0, Tb = 100, Tend = 140

function tempAt(q: number): number {
  if (q <= SEG[1]) return T0 + (Tm - T0) * (q - SEG[0]) / (SEG[1] - SEG[0])
  if (q <= SEG[2]) return Tm
  if (q <= SEG[3]) return Tm + (Tb - Tm) * (q - SEG[2]) / (SEG[3] - SEG[2])
  if (q <= SEG[4]) return Tb
  return Tb + (Tend - Tb) * (q - SEG[4]) / (SEG[5] - SEG[4])
}
function stateAt(q: number): { label: string; cls: string } {
  if (q < SEG[1]) return { label: 'Rắn (đang nóng lên)', cls: 'bg-blue-600' }
  if (q < SEG[2]) return { label: 'Đang nóng chảy', cls: 'bg-cyan-600' }
  if (q < SEG[3]) return { label: 'Lỏng (đang nóng lên)', cls: 'bg-teal-600' }
  if (q < SEG[4]) return { label: 'Đang sôi / hoá hơi', cls: 'bg-orange-500' }
  return { label: 'Hơi', cls: 'bg-red-500' }
}

const HeatingCurveSimulation: React.FC<Props> = ({
  title = 'Đường cong Nhiệt độ – Nhiệt lượng',
  description = 'Cung cấp nhiệt lượng Q cho chất: nhiệt độ tăng rồi đi ngang khi chuyển thể (nóng chảy, hoá hơi).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [q, setQ] = useState(0)
  const firstRef = useRef(false)
  const W = 680, H = 320

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const x0 = 58, y0 = H - 36, gw = W - x0 - 22, gh = H - 60
    const X = (qq: number) => x0 + qq * gw
    const Y = (t: number) => y0 - ((t - T0) / (Tend - T0)) * gh
    // lưới
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
    for (let i = 0; i <= 10; i++) { const gx = x0 + (i / 10) * gw; ctx.beginPath(); ctx.moveTo(gx, 20); ctx.lineTo(gx, y0); ctx.stroke() }
    // trục ĐẬM + mũi tên
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2.5; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, 14); ctx.lineTo(x0, y0); ctx.lineTo(x0 + gw, y0); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, 7); ctx.lineTo(x0 - 5, 17); ctx.lineTo(x0 + 5, 17); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(x0 + gw + 7, y0); ctx.lineTo(x0 + gw - 4, y0 - 5); ctx.lineTo(x0 + gw - 4, y0 + 5); ctx.closePath(); ctx.fill()
    // nhãn trục
    ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'right'
    ctx.fillText('Thời gian (s)', x0 + gw + 2, y0 + 18); ctx.textAlign = 'left'
    ctx.save(); ctx.translate(14, (14 + y0) / 2); ctx.rotate(-Math.PI / 2); ctx.textAlign = 'center'; ctx.fillText('Nhiệt độ (°C)', 0, 0); ctx.restore(); ctx.textAlign = 'left'
    // mốc T_nc (nóng chảy) & T_s (sôi) — nhãn bên TRÁI trục, có chỉ số dưới
    const subLbl = (sub: string, y: number) => {
      ctx.fillStyle = '#334155'; ctx.font = 'bold 13px sans-serif'
      ctx.fillText('T', x0 - 28, y + 4)
      ctx.font = '9px sans-serif'; ctx.fillText(sub, x0 - 28 + ctx.measureText('T').width + 6, y + 7)
    }
    ctx.strokeStyle = 'rgba(148,163,184,0.6)'; ctx.setLineDash([4, 3]); ctx.lineWidth = 1
    ;[{ t: Tm, s: 'nc' }, { t: Tb, s: 's' }].forEach(({ t, s }) => {
      ctx.beginPath(); ctx.moveTo(x0, Y(t)); ctx.lineTo(x0 + gw, Y(t)); ctx.stroke(); subLbl(s, Y(t))
    })
    ctx.setLineDash([])
    // đường cong (đậm)
    // CHỈ vẽ phần đã đạt tới Q hiện tại (phía SAU điểm) — không hiện đường cong phía trước
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 3.5; ctx.beginPath()
    const iMaxH = Math.max(0, Math.ceil(q * 200))
    for (let i = 0; i <= iMaxH; i++) { const qq = Math.min(q, i / 200); const px = X(qq), py = Y(tempAt(qq)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
    ctx.stroke()
    // điểm chạy
    const px = X(q), py = Y(tempAt(q))
    ctx.strokeStyle = 'rgba(239,68,68,0.5)'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3])
    ctx.beginPath(); ctx.moveTo(px, y0); ctx.lineTo(px, py); ctx.moveTo(x0, py); ctx.lineTo(px, py); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#ef4444'; ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill()
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke()
  }, [q])

  useEffect(() => { draw() }, [draw])

  const st = stateAt(q)
  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex items-center justify-center gap-3 my-3">
        <span className="text-sm text-gray-500">Nhiệt độ: <b className="text-blue-700">{tempAt(q).toFixed(0)}°C</b></span>
        <span className={`px-3 py-1 rounded-full font-bold text-white ${st.cls}`}>{st.label}</span>
      </div>
      <label className="flex items-center gap-2 text-sm"><span className="w-32 text-gray-600 font-semibold">Thời gian (đun)</span>
        <input type="range" min={0} max={1} step={0.01} value={q}
          onChange={(e) => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } setQ(Number(e.target.value)) }}
          className="flex-1 accent-red-500" />
        <span className="w-12 text-right font-semibold">{Math.round(q * 100)}%</span></label>
    </div>
  )
}
export default HeatingCurveSimulation
